/**
 * Aurora design-system v2.8 — UI-примітиви.
 *
 * Усі /admin/* + керівні дашборди використовують ці компоненти. Робочі
 * ролі (швея/ОТК/закрійник/пакувальник) лишаються на legacy mes-* стилях.
 */
import type { ReactNode } from "react";

// ---------- Glass Card ----------

interface GlassCardProps {
  children: ReactNode;
  className?: string;
  padding?: "none" | "sm" | "md" | "lg";
  /** Якір для `scrollIntoView` — коли треба довести людину до конкретної
   *  картки (наприклад, вона перемістилась у списку після зміни статусу). */
  id?: string;
}

export function GlassCard({
  children,
  className = "",
  padding = "md",
  id,
}: GlassCardProps) {
  const pad =
    padding === "none"
      ? ""
      : padding === "sm"
      ? "p-3"
      : padding === "lg"
      ? "p-6"
      : "p-4";
  return <div id={id} className={`glass ${pad} ${className}`}>{children}</div>;
}

// ---------- Chip ----------

export type ChipVariant =
  | "neutral"
  | "primary"
  | "pink"
  | "success"
  | "warning"
  | "danger";

// Насиченіший фон + рамка — щоб чип не зливався зі скляним/світлим тлом.
const CHIP_STYLE: Record<ChipVariant, { bg: string; color: string; border: string; dot?: string }> = {
  neutral: { bg: "rgba(107,112,128,0.20)", color: "#3c4150", border: "rgba(107,112,128,0.45)" },
  primary: { bg: "rgba(91,109,255,0.22)", color: "#3442c4", border: "rgba(91,109,255,0.55)", dot: "#5B6DFF" },
  pink: { bg: "rgba(255,91,167,0.20)", color: "#b0316e", border: "rgba(255,91,167,0.5)", dot: "#FF5BA7" },
  success: { bg: "rgba(29,171,92,0.22)", color: "#0c6a37", border: "rgba(29,171,92,0.5)", dot: "#1DAB5C" },
  warning: { bg: "rgba(247,165,0,0.24)", color: "#875c00", border: "rgba(247,165,0,0.55)", dot: "#F7A500" },
  danger: { bg: "rgba(229,72,77,0.22)", color: "#b0242a", border: "rgba(229,72,77,0.5)", dot: "#E5484D" },
};

interface ChipProps {
  children: ReactNode;
  variant?: ChipVariant;
  showDot?: boolean;
  className?: string;
}

export function Chip({
  children,
  variant = "neutral",
  showDot = false,
  className = "",
}: ChipProps) {
  const s = CHIP_STYLE[variant];
  return (
    <span
      // whitespace-nowrap: чип має фіксовану висоту h-6, і перенос тексту
      // вилазив за рамку («прострочено 96 / дн», iPhone, 29.09.2026).
      className={`inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full text-xs font-semibold whitespace-nowrap flex-none ${className}`}
      style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}` }}
    >
      {showDot && s.dot && (
        <span
          className="w-[7px] h-[7px] rounded-full"
          style={{ background: s.dot }}
        />
      )}
      {children}
    </span>
  );
}

// ---------- KPI Tile (drill-down фільтр для дашбордів) ----------

interface KpiProps {
  label: string;
  value: ReactNode;
  sub?: string;
  color?: string;       // hex; default — aurora.ink
  active?: boolean;
  onClick?: () => void;
}

export function Kpi({
  label,
  value,
  sub,
  color = "#1B1B1F",
  active = false,
  onClick,
}: KpiProps) {
  const ringBg = active ? `${color}14` : "rgba(255,255,255,0.45)";
  const border = active ? `2px solid ${color}` : "1px solid rgba(255,255,255,0.6)";
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      onClick={onClick}
      className={`text-left p-4 rounded-2xl shadow-glass backdrop-blur-glass ${onClick ? "cursor-pointer hover:brightness-105 transition-all" : ""}`}
      style={{ background: ringBg, border, backdropFilter: "blur(20px)" }}
    >
      <div className="text-xs text-aurora-muted font-semibold mb-1.5">{label}</div>
      <div
        className="text-2xl font-extrabold tracking-tight"
        style={{ color }}
      >
        {value}
      </div>
      {sub && <div className="text-xs text-aurora-muted mt-1">{sub}</div>}
    </Tag>
  );
}

// ---------- Buttons ----------

interface AuroraButtonProps {
  children: ReactNode;
  onClick?: () => void;
  type?: "button" | "submit" | "reset";
  variant?: "primary" | "ghost" | "danger" | "success";
  size?: "sm" | "md" | "lg";
  disabled?: boolean;
  fullWidth?: boolean;
  icon?: ReactNode;
  className?: string;
  /** Дія триває: кнопка блокується й показує кружечок замість іконки.
   *  Окремо від `disabled`, бо це РІЗНІ речі: «недоступно» і «вже працюю».
   *  Доти обидва виглядали як бліда кнопка — і на слабкому Wi-Fi це
   *  провокувало друге натискання. */
  busy?: boolean;
}

const BTN_VARIANT: Record<
  NonNullable<AuroraButtonProps["variant"]>,
  { bg: string; color: string; border: string; shadow: string }
> = {
  primary: {
    bg: "#5B6DFF",
    color: "#fff",
    border: "none",
    shadow: "0 8px 18px rgba(91,109,255,0.25)",
  },
  ghost: {
    bg: "rgba(255,255,255,0.65)",
    color: "#1B1B1F",
    border: "1px solid rgba(0,0,0,0.1)",
    shadow: "none",
  },
  danger: {
    bg: "#E5484D",
    color: "#fff",
    border: "none",
    shadow: "0 8px 18px rgba(229,72,77,0.25)",
  },
  success: {
    bg: "#1DAB5C",
    color: "#fff",
    border: "none",
    shadow: "0 8px 18px rgba(29,171,92,0.25)",
  },
};

// Висота НЕ опускається нижче 44 px у жодного розміру: це поріг для пальця,
// а цех працює на планшетах стоячи. `sm` лишається меншим за `md` — вужчими
// полями й дрібнішим текстом, — але в нього так само можна влучити.
// Куплено ревізією 04.09: `sm` (36 px) стояв ~38 разів по проєкту, тобто був
// найдешевшим важелем і найбільшою купою промахів одночасно.
const BTN_SIZE = {
  sm: "h-11 px-3 text-sm",
  md: "h-11 px-4 text-[15px]",
  lg: "h-12 px-5 text-base",
};

/** Кружечок «працюю». Свій, а не з бібліотеки: одна `border` + `animate-spin`
 *  із Tailwind. `currentColor` — щоб він жив у будь-якому варіанті кнопки. */
function Spinner() {
  return (
    <span
      aria-hidden="true"
      className="inline-block w-4 h-4 rounded-full border-2 border-current border-r-transparent animate-spin"
    />
  );
}

export function AuroraButton({
  children,
  onClick,
  type = "button",
  variant = "primary",
  size = "md",
  disabled = false,
  fullWidth = false,
  icon,
  className = "",
  busy = false,
}: AuroraButtonProps) {
  const v = BTN_VARIANT[variant];
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || busy}
      className={`${BTN_SIZE[size]} rounded-[10px] font-bold inline-flex items-center justify-center gap-2 transition-[filter,transform] duration-100 hover:brightness-110 active:scale-[0.97] active:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:brightness-100 disabled:active:scale-100 ${fullWidth ? "w-full" : ""} ${className}`}
      style={{
        background: v.bg,
        color: v.color,
        border: v.border,
        boxShadow: v.shadow,
      }}
    >
      {busy ? <Spinner /> : icon}
      {children}
    </button>
  );
}

